// Roll based access control utilities

export enum UserRole {
    ADMIN = 'admin',
    OPERATION = 'operation',
    VENDOR = 'vendor'
  }
  
  export interface User {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    groups: string[];
  }
  
  // Check if user has a specific role
  export const hasRole = (user: User | null, role: UserRole): boolean => {
    if (!user || !user.groups) return false;
    return user.groups.includes(role);
  };
  
  // Check if user has any of the specified roles
  export const hasAnyRole = (user: User | null, roles: UserRole[]): boolean => {
    if (!user || !user.groups) return false;
    return roles.some(role => user.groups.includes(role));
  };
  
  // Check if user has all of the specified roles
  export const hasAllRoles = (user: User | null, roles: UserRole[]): boolean => {
    if (!user || !user.groups) return false;
    return roles.every(role => user.groups.includes(role));
  };
  
  // Role hierarchy checks (based on precedence: admin=1, operation=2, vendor=3)
  export const isAdmin = (user: User | null): boolean => hasRole(user, UserRole.ADMIN);
  export const isOperation = (user: User | null): boolean => hasRole(user, UserRole.OPERATION);
  export const isVendor = (user: User | null): boolean => hasRole(user, UserRole.VENDOR);
  
  // Permission checks based on your requirements
  export const canAddVendor = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };
  
  export const canAssociateQRCode = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };
  
  export const canGenerateQRCode = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };
  
  export const canAddUsers = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };
  
  export const canAccessAdminDashboard = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION, UserRole.VENDOR]);
  };

  export const canDeleteVendor = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };

  export const canConfirmVendor = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canManagePricing = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };

  export const canViewPricing = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canEditMenuAvailability = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION, UserRole.VENDOR]);
  };

  export const canViewAnalytics = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };

  export const canManageProductCategories = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canViewProductCategories = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canDeleteProductCategories = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };

  export const canPlaceOrders = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canViewOrders = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canAccessVendorOrderPlacement = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canProcessPayments = (user: User | null): boolean => {
    return hasAnyRole(user, [UserRole.ADMIN, UserRole.OPERATION]);
  };

  export const canViewQrCodeDashboard = (user: User | null): boolean => {
    return hasRole(user, UserRole.ADMIN);
  };

  // Get user's highest role (based on precedence)
  export const getUserHighestRole = (user: User | null): UserRole | null => {
    if (!user || !user.groups) return null;
    
    if (user.groups.includes(UserRole.ADMIN)) return UserRole.ADMIN;
    if (user.groups.includes(UserRole.OPERATION)) return UserRole.OPERATION;
    if (user.groups.includes(UserRole.VENDOR)) return UserRole.VENDOR;
    
    return null;
  };
  
  // Get all user roles
  export const getUserRoles = (user: User | null): UserRole[] => {
    if (!user || !user.groups) return [];
    return user.groups.filter(group => 
      Object.values(UserRole).includes(group as UserRole)
    ) as UserRole[];
  };